import { registerFunctionalComponent } from 'js/lib/components/component'
import { useListener } from 'js/lib/dynamic/listener'
import { push } from 'js/lib/history'

const CLICKABLE_TAGS = ['A', 'BUTTON']
const NAVIGATION_KEYS = ['href', 'url', 'action']

registerFunctionalComponent('.js-c-navigate', 'DynamicNavigate', DynamicNavigate)

function DynamicNavigate(component) {
  const { element } = component

  if (element.dataset.trigger) {
    component.useHandler(() => useTriggerNavigation(element))

    return
  }

  component.useHandler(() => useClickNavigation(element))
}

function useTriggerNavigation(element) {
  const listenerAst = JSON.parse(element.dataset.trigger)
  const { onChange } = useListener(listenerAst)

  return onChange(event => navigateToResolvedUrl(element.dataset.href, getEventNavigationValue(event)))
}

function useClickNavigation(element) {
  const handleClick = event => {
    const clickableElement = findClickableElement(event.target, element)
    const navigationValue = getNavigationValue(clickableElement)
    const url = resolveNavigationUrl(element.dataset.href, navigationValue)

    if (!url) return

    if (clickableElement?.tagName === 'A') {
      event.preventDefault()
    }

    push({}, '', url)
  }

  element.addEventListener('click', handleClick)

  return () => element.removeEventListener('click', handleClick)
}

function navigateToResolvedUrl(overrideValue, navigationValue) {
  const url = resolveNavigationUrl(overrideValue, navigationValue)
  if (!url) return

  push({}, '', url)
}

function resolveNavigationUrl(overrideValue, clickableValue) {
  if (overrideValue) {
    // Query-only overrides extend the clicked URL instead of replacing it,
    // so wrappers can add or update params without losing the base path.
    return queryParamsOnly(overrideValue) ? appendQueryParams(clickableValue, overrideValue) : overrideValue
  }

  if (!clickableValue) return null

  return queryParamsOnly(clickableValue)
    ? appendQueryParams(window.location.pathname + window.location.search, clickableValue)
    : clickableValue
}

function queryParamsOnly(value) {
  return typeof value === 'string' && value.startsWith('?')
}

function appendQueryParams(baseValue, queryValue) {
  const baseUrl = new URL(baseValue || currentLocationPath(), window.location.origin)
  const incomingParams = new URLSearchParams(queryValue.replace(/^\?/, ''))
  const incomingKeys = [...new Set([...incomingParams.keys()])]

  incomingKeys.forEach(key => baseUrl.searchParams.delete(key))
  incomingParams.forEach((value, key) => baseUrl.searchParams.append(key, value))

  return `${baseUrl.pathname}${baseUrl.search}${baseUrl.hash}`
}

function getEventNavigationValue(event) {
  return getNavigationValue(event?.target) || getObjectNavigationValue(event?.detail)
}

function currentLocationPath() {
  return window.location.pathname + window.location.search
}

function findClickableElement(target, boundary) {
  let currentElement = target

  while (currentElement && currentElement !== boundary) {
    if (hasNavigationValue(currentElement) || CLICKABLE_TAGS.includes(currentElement.tagName)) {
      return currentElement
    }

    currentElement = currentElement.parentElement
  }

  return null
}

function hasNavigationValue(element) {
  return Boolean(getNavigationValue(element))
}

function getNavigationValue(source) {
  if (!source || typeof source !== 'object') return null

  if (typeof source.getAttribute === 'function') {
    return getElementNavigationValue(source)
  }

  return getObjectNavigationValue(source)
}

function getElementNavigationValue(element) {
  for (const key of NAVIGATION_KEYS) {
    const value = element.getAttribute(key) || element.dataset?.[key]
    if (value) return value
  }

  return null
}

function getObjectNavigationValue(source = {}) {
  for (const key of NAVIGATION_KEYS) {
    const value = source[key]
    if (value) return value
  }

  return null
}
